import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

export interface ChatMessage {
  question: string;
  lead_id: string;
  source: string;
  variant_id: string;
  session_uuid?: string;
  question_by_bb?: string;
  answer_by_student?: string;
}

export interface ChatResponse {
  question: string;
  answer: string;
  options: string[];
  timestamp: number;
  has_latex: boolean;
  source: string;
  student_grade: string;
  student_age: number;
  subject: string;
  topic: string;
  subtopic: string;
  answer_source: string;
  session_uuid: string;
  user_device: string;
  paid_user: boolean;
  // Quick Challenge specific fields
  question_by_bb?: string;
  answer_by_student?: string;
}

export const sendChatMessage = (data: ChatMessage): Promise<ChatResponse> => {
  return axios.post('/doubtgpt/v1/answer_all_questions', data);
};

export interface FeedbackMessage {
  lead_id: string;
  feedback: string | boolean;
  version: string | number;
}

export interface FeedbackResponse {
  status: string;
}

export const sendFeedback = (
  data: FeedbackMessage
): Promise<FeedbackResponse> => {
  return axios.post('/doubtgpt/v1/feedback', data);
};

export const sendChatMessageMockHandler = http.post(
  `${env.API_URL}/doubtgpt/v1/answer_all_questions`,
  async ({ request }): Promise<HttpResponse<ChatResponse>> => {
    const data = (await request.json()) as ChatMessage;

    await networkDelay();

    // Check if this is a QuickChallenge request
    const isQuickChallenge = !!(data.question_by_bb && data.answer_by_student);

    const mockResponse: ChatResponse = {
      question: data.question,
      answer: isQuickChallenge
        ? `✅ Correct! 25% of 80 is indeed ${data.answer_by_student}. Well done! 🎉`
        : `Hello! 1 + 1 does not equal 3. The correct answer is: 1 + 1 = 2. Feel free to ask more math questions if you have any!`,
      options: [],
      timestamp: Date.now(),
      has_latex: false,
      source: 'explain_me',
      student_grade: '4-6',
      student_age: 10,
      subject: 'Math',
      topic: 'Arithmetic',
      subtopic: 'Addition',
      answer_source: 'GPT assistant',
      session_uuid: data.session_uuid ?? `session-${Date.now()}`,
      user_device: 'desktop',
      paid_user: true,
      // Include QC fields if present
      ...(data.question_by_bb && { question_by_bb: data.question_by_bb }),
      ...(data.answer_by_student && {
        answer_by_student: data.answer_by_student,
      }),
    };

    return HttpResponse.json(mockResponse);
  }
);

export const sendFeedbackMockHandler = http.post(
  `${env.API_URL}/doubtgpt/v1/feedback`,
  async (): Promise<HttpResponse<FeedbackResponse>> => {
    await networkDelay();

    return HttpResponse.json({ status: 'Update success' });
  }
);
